import type { Cents } from '../../shared/money/cents.js'
import type { CivilDate } from '../../shared/date/civil.js'

/**
 * A nota fiscal. Transcrição de `Docs/02-Dominio/Receipt.md` e
 * `Docs/02-Dominio/ReceiptItem.md`.
 *
 * ## `extracted` é o estado que sustenta o princípio 5
 *
 * *"Os itens já existem, cada um com produto sugerido e confiança, e **nada foi
 * aplicado ainda**. Só a transição para `confirmed` gera breakdown e pontos de
 * histórico."* É o princípio 5 materializado num campo de status: enquanto está
 * `extracted`, a IA não mexeu em nada financeiro.
 *
 * ## `rawText` é imutável, e é isso que faz o matching melhorar
 *
 * *"É a evidência. Se o usuário corrige o vínculo, o que muda é `productId`."*
 * Editar o texto cru destruiria a única coisa capaz de fazer o matching
 * melhorar com o tempo — e é a chave de `learnFrom`.
 */

export type ReceiptStatus = 'uploaded' | 'processing' | 'extracted' | 'confirmed' | 'failed'

export type MatchStatus = 'pending' | 'confirmed' | 'rejected' | 'skipped'

/** O que a IA (ou o QR) leu do cabeçalho da nota. */
export type ExtractedHeader = {
  merchantName: string | null
  merchantCnpj: string | null
  issuedAt: CivilDate | null
  totalCents: Cents | null
  itemCount: number
  /** A chave da NFC-e, quando o QR foi legível. */
  accessKey: string | null
}

export type Receipt = {
  id: string
  householdId: string
  /** A transação que esta nota detalha. `null` é aceitável e útil — ver abaixo. */
  transactionId: string | null
  /**
   * A categoria **do documento inteiro**, quando ele é de uma coisa só.
   *
   * ## Ela é o PISO, e não o teto
   *
   * A ordem continua sendo linha → produto → nota. A da linha é a mais
   * específica e sempre manda; a do produto é o padrão do catálogo; e esta
   * pega o que ninguém classificou. Numa conta de restaurante — quatro linhas
   * sem produto e sem categoria — ela classifica as quatro de uma vez. Num
   * cupom de mercado, onde cada produto tem a sua, ela quase nunca é usada, e é
   * assim que tem de ser: o piso não sobrepõe quem já sabe de si.
   *
   * ## E ela viaja para a TRANSAÇÃO no vínculo
   *
   * Uma nota que diz "isto é restaurante" sabe classificar a compra que a
   * originou. No vínculo, a transação **sem categoria** recebe esta; a que já
   * tem uma **não é tocada** — sobrescrever em silêncio uma escolha feita à mão
   * é o tipo de ajuda que só se descobre depois, num número que mudou sozinho.
   */
  categoryId: string | null
  imageStoragePath: string
  thumbnailUrl: string | null
  status: ReceiptStatus
  extracted: ExtractedHeader | null
  /** Qual provider processou. Para saber a quem cobrar um resultado ruim. */
  provider: string | null
  processedAt: string | null
  error: string | null
  createdAt: string
  updatedAt: string
}

export type ReceiptItem = {
  id: string
  householdId: string
  receiptId: string
  lineNumber: number

  /**
   * A linha que ninguém leu de papel nenhum: a pessoa a acrescentou à mão.
   *
   * ## O que ela existe para dizer
   *
   * O cupom cobra coisas que não são mercadoria — frete, taxa de serviço,
   * embalagem — e às vezes abate no fim. Numa nota de R$ 124,17 cujos itens
   * somavam R$ 102,18, os R$ 21,99 da entrega eram uma "diferença" sem dono: a
   * revisão avisava que a leitura devia estar errada, e não estava.
   *
   * ## Ela é a ÚNICA que pode valer negativo
   *
   * Valor negativo numa linha lida é devolução, e `confirmReceipt` a toma em
   * módulo para o resto do breakdown não crescer acima do total. A manual
   * escapa disso porque o sinal dela foi digitado de propósito — é o que
   * separa "isto custou" de "isto abateu".
   *
   * ## `rawText` dela é o nome que a pessoa deu
   *
   * O campo abaixo diz "exatamente como saiu da nota", e aqui não saiu de nota
   * nenhuma. A consequência prática: não há evidência a preservar, e por isso
   * esta é a única linha que a tela deixa **apagar** em vez de marcar como
   * inexistente.
   */
  manual: boolean

  // ── CRU: exatamente como saiu da nota. Nunca editado. ──
  rawText: string
  rawCode: string | null
  quantity: number
  unit: string | null
  unitPriceCents: Cents
  totalCents: Cents
  barcode: string | null
  /**
   * O desconto desta linha, em centavos **positivos**. Zero quando não há.
   *
   * ## Por que ele mora aqui, e não no cabeçalho
   *
   * O cupom da NFC-e imprime `desconto item 001` logo abaixo do item 001 — o
   * abatimento é **daquele item**, e não do cupom. Foi assim que ele apareceu:
   * uma Coca de R$ 5,99 com −R$ 0,80, num cupom cujo `VALOR TOTAL` era 250,73
   * e cujo `VALOR A PAGAR` era 249,93. Sem este campo, os 80 centavos viravam
   * uma "diferença" sem dono na revisão, e o histórico de preço guardava 5,99
   * por uma Coca que custou 5,19.
   *
   * ## Ele NÃO edita o cru
   *
   * `totalCents` continua sendo o que está impresso na linha — é a evidência, e
   * evidência não se corrige. O desconto fica ao lado, e quem soma usa
   * `valorEfetivo`. Um cupom conferido contra a tela precisa bater linha a
   * linha com o papel.
   */
  discountCents: Cents

  // ── RESOLUÇÃO ──
  productId: string | null
  matchId: string | null
  matchStatus: MatchStatus
  categoryId: string | null
  /**
   * Se a pessoa **mudou** o vínculo nesta revisão.
   *
   * Só a correção vira aprendizado: aceitar o que já resolvia não ensina nada
   * que já não se saiba, e gravaria um `ProductMatch` `manual` por cima de um
   * `learned` idêntico em toda nota. É o que `confirmReceipt` consome, e é o que
   * faz o item 2 da definição de pronto da fase valer — *"corrigir um vínculo
   * faz o mesmo `rawText` resolver sozinho na nota seguinte"*.
   */
  corrected: boolean

  /**
   * O que a pessoa corrigiu por cima do lido. `null` quando ela não editou.
   *
   * ## Ao lado do cru, e não por cima
   *
   * A leitura erra — no cupom do Muffato ela pôs o preço de um item no vizinho
   * (decisão 284) —, e confirmar assim grava preço errado no histórico. Então é
   * preciso corrigir nome, quantidade e valores. Mas o cru é a evidência
   * conferida contra o papel e a chave do aprendizado: sobrescrevê-lo apagaria
   * o que a IA leu, e ninguém saberia mais o que foi corrigido. A correção mora
   * aqui, e quem soma usa `valoresDaLinha` e `valorEfetivo`.
   */
  edited: ItemEdit | null

  createdAt: string
  updatedAt: string
}

/**
 * A correção de uma linha. Os números vão **todos juntos**, e não um por um:
 * quantidade, unitário e total são uma conta só, e corrigir o total deixando o
 * unitário lido faria a linha "não fechar" por causa da própria correção.
 */
export type ItemEdit = {
  /**
   * O nome que a pessoa deu à linha. `null` mantém o do produto, ou o lido.
   * Vale só para esta linha: renomear o produto é na tela de Produtos.
   */
  name: string | null
  quantity: number
  unitPriceCents: Cents
  totalCents: Cents
}

/** Quantidade, unitário e total que VALEM: os corrigidos, ou os lidos. */
export function valoresDaLinha(
  linha: Pick<ReceiptItem, 'quantity' | 'unitPriceCents' | 'totalCents' | 'edited'>,
): Pick<ItemEdit, 'quantity' | 'unitPriceCents' | 'totalCents'> {
  const fonte = linha.edited ?? linha
  return {
    quantity: fonte.quantity,
    unitPriceCents: fonte.unitPriceCents,
    totalCents: fonte.totalCents,
  }
}

/**
 * O `edited` gravado, ou `null` quando não há — ou quando o que há não é uma
 * correção inteira.
 *
 * **Inválido vira `null`, e não erro.** Uma correção quebrada não pode derrubar
 * a revisão da nota inteira; a linha volta a mostrar o lido, que é a evidência,
 * e a pessoa corrige de novo. Mora no domínio porque o app e a Function leem o
 * mesmo campo, e dois conversores discordariam sobre o que é válido.
 */
export function editadoDe(valor: unknown): ItemEdit | null {
  if (typeof valor !== 'object' || valor === null) return null
  const v = valor as Record<string, unknown>
  const inteiro = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0
  if (typeof v['quantity'] !== 'number' || !(v['quantity'] > 0)) return null
  if (!inteiro(v['unitPriceCents']) || !inteiro(v['totalCents'])) return null
  const nome = typeof v['name'] === 'string' && v['name'].trim() !== '' ? v['name'].trim() : null
  return {
    name: nome,
    quantity: v['quantity'],
    unitPriceCents: v['unitPriceCents'],
    totalCents: v['totalCents'],
  }
}

/**
 * A tolerância de `quantity * unitPriceCents ≈ totalCents`.
 *
 * **Um centavo**, e o motivo está na nota: *"arredondamento de balança em itens
 * vendidos por peso"*. 0,412 kg a R$ 24,90 dá R$ 10,2588 — o PDV escreve
 * R$ 10,26, e a multiplicação daqui dá R$ 10,2588. Exigir igualdade exata
 * marcaria como inconsistente toda linha de hortifrúti.
 */
export const TOLERANCIA_LINHA: Cents = 1

/** A linha fecha? Ver `TOLERANCIA_LINHA`. */
export function lineIsConsistent(item: Pick<ReceiptItem, 'quantity' | 'unitPriceCents' | 'totalCents'>): boolean {
  const esperado = item.quantity * item.unitPriceCents
  return Math.abs(esperado - item.totalCents) <= TOLERANCIA_LINHA
}

export type ReviewTotals = {
  /** A soma das linhas que não foram rejeitadas. */
  linesCents: Cents
  /** O total que a nota declara. `null` quando a extração não o leu. */
  receiptCents: Cents | null
  /**
   * A diferença, quando dá para calcular. **Positiva** quando a nota é maior
   * que a soma — desconto, ou item ilegível.
   */
  differenceCents: Cents | null
  /** Quantas linhas ainda esperam decisão. */
  pendingCount: number
}

/**
 * O que a tela de revisão mostra no rodapé.
 *
 * *"A diferença entre a soma dos itens e o total da nota aparece explícita"* —
 * entrega "Revisão" da fase, e invariante de `Receipt.md`: *"geralmente é
 * desconto ou item ilegível, e o usuário resolve"*.
 *
 * **Linha `rejected` sai da soma; `skipped` FICA.** É a diferença que
 * `ReceiptItem.md` desenha: *"`skipped` sai do catálogo mas continua contando no
 * total da nota"* — sacola plástica é dinheiro que saiu. `rejected` é a linha
 * que a extração leu errado e não existe.
 */
/**
 * O que uma linha vale de verdade: o impresso menos o desconto dela.
 *
 * É esta a conta que fecha com o `VALOR A PAGAR` do cupom, e é ela que vira
 * preço no catálogo — o histórico existe para responder "quanto custou", e o
 * que custou é o que saiu da conta.
 *
 * **O impresso é o corrigido, quando há correção** — ver `ReceiptItem.edited`.
 */
export function valorEfetivo(
  linha: Pick<ReceiptItem, 'totalCents' | 'discountCents' | 'edited'>,
): Cents {
  return (linha.edited?.totalCents ?? linha.totalCents) - linha.discountCents
}

/**
 * A categoria que vai VALER para uma linha, na confirmação e na tela.
 *
 * Três degraus, do mais específico para o mais geral:
 *
 * 1. a **da linha**, escolhida à mão nesta revisão;
 * 2. a **do produto**, e só quando o vínculo está confirmado — um produto
 *    apenas *sugerido* ainda não é o produto daquela linha;
 * 3. a **da nota**, que é o piso: ela pega o que ninguém classificou.
 *
 * **Ela mora no domínio porque duas cópias divergiriam.** A tela mostra uma
 * categoria e a Cloud Function grava o breakdown com outra — e ninguém veria,
 * porque as duas parecem certas cada uma no seu lugar. O comentário que já
 * existia em `Nota.tsx` avisava disso; agora é a mesma função nos dois.
 */
export function categoriaEfetiva(
  linha: Pick<ReceiptItem, 'categoryId' | 'matchStatus'>,
  produto: { categoryId: string | null } | null,
  categoriaDaNota: string | null = null,
): string | null {
  if (linha.categoryId !== null) return linha.categoryId
  if (linha.matchStatus === 'confirmed' && produto?.categoryId != null) return produto.categoryId
  return categoriaDaNota
}

/**
 * Quanto cada categoria já soma nesta nota — o **teto de um abatimento** nela.
 *
 * ## Por que ela existe
 *
 * A linha manual negativa abate de uma categoria (ver `ReceiptItem.manual`), e
 * `confirmReceipt` **recusa** a categoria que fecharia negativa. Essa recusa
 * acontece na Cloud Function, depois de a nota já estar confirmada: a pessoa
 * veria uma nota confirmada e um detalhamento que nunca chegou, sem nada
 * dizendo por quê. Com esta função a tela cobra antes, no formulário, onde
 * ainda dá para consertar.
 *
 * Mesma composição da confirmação — `categoriaEfetiva` e `valorEfetivo`, e não
 * uma segunda conta —, porque duas contas para o mesmo dinheiro divergiriam e a
 * tela aprovaria o que a Function recusa.
 *
 * **`rejected` fica de fora** (a linha não existe) e a sem categoria também: ela
 * não é teto de nada, vai para o resto.
 */
export function totalPorCategoria(
  linhas: Pick<
    ReceiptItem,
    'categoryId' | 'matchStatus' | 'totalCents' | 'discountCents' | 'edited' | 'productId'
  >[],
  produtoDa: (productId: string) => { categoryId: string | null } | null,
  categoriaDaNota: string | null,
): Map<string, Cents> {
  const por = new Map<string, Cents>()
  for (const linha of linhas) {
    if (linha.matchStatus === 'rejected') continue
    const categoria = categoriaEfetiva(
      linha,
      linha.productId === null ? null : produtoDa(linha.productId),
      categoriaDaNota,
    )
    if (categoria === null) continue
    por.set(categoria, (por.get(categoria) ?? 0) + valorEfetivo(linha))
  }
  return por
}

export function reviewTotals(
  items: Pick<ReceiptItem, 'totalCents' | 'discountCents' | 'matchStatus' | 'edited'>[],
  receiptCents: Cents | null,
): ReviewTotals {
  const contam = items.filter((i) => i.matchStatus !== 'rejected')
  const linesCents = contam.reduce((soma, i) => soma + valorEfetivo(i), 0)
  return {
    linesCents,
    receiptCents,
    differenceCents: receiptCents === null ? null : receiptCents - linesCents,
    pendingCount: items.filter((i) => i.matchStatus === 'pending').length,
  }
}

/**
 * A soma dos itens está LONGE do total da nota — longe o bastante para ser
 * leitura errada, e não um desconto sem dono.
 *
 * ## O piso e a proporção, e de onde saem
 *
 * Diferença pequena é o caso comum e inofensivo: um desconto que o cupom não
 * atribuiu a item nenhum (os R$ 0,80 da Coca, 0,3% da nota) ou arredondamento.
 * A tela explica isso com calma, e confirmar assim é seguro. Diferença grande é
 * outra coisa: na nota do Muffato (decisão 284) a leitura pôs preço no item
 * vizinho e perdeu quantidades, e a soma deu R$ 615,46 contra R$ 725,59 — 15%.
 * Confirmar isso grava preço errado no histórico de quase todos os produtos.
 *
 * **Mais de 2% da nota, e nunca menos de R$ 1,00.** O piso existe para a nota
 * pequena: numa de R$ 10, 2% são 20 centavos, e alarme por centavos ensina a
 * ignorar o alarme.
 */
export function divergenciaGrande(totais: ReviewTotals): boolean {
  if (totais.receiptCents === null || totais.differenceCents === null) return false
  const tolerancia = Math.max(100, Math.round(totais.receiptCents * 0.02))
  return Math.abs(totais.differenceCents) > tolerancia
}

/** Uma transação candidata a ser a que esta nota detalha. */
export type ReconcileCandidate = {
  transactionId: string
  /** Quantos dias de distância entre a nota e a transação. Menor é melhor. */
  daysApart: number
}

/**
 * A janela de conciliação. **Três dias**, como manda `Receipt.md`.
 *
 * A nota é do dia da compra; a transação do cartão pode ser postada dias depois.
 * Três cobre o atraso normal sem alcançar a compra da semana seguinte no mesmo
 * mercado — que é exatamente o falso positivo que faria a nota grudar na
 * transação errada.
 */
export const JANELA_DIAS = 3

/**
 * A janela LARGA: trinta dias.
 *
 * ## Ela não substitui a de três — soma-se a ela
 *
 * A de três continua sendo a que dá confiança, e o motivo acima continua
 * inteiro. O que a prática mostrou é que ela **acha pouco**: visto na tela de
 * uma nota real do FESTVAL, "nenhuma transação com o mesmo valor nos três dias
 * em volta", com a lista inteira do mês aberta para a pessoa procurar à mão.
 *
 * A transação demora a chegar. O sync roda uma vez por dia, a fatura do cartão
 * posta quando posta, e a compra de 20 dias atrás com **exatamente** o mesmo
 * valor quase sempre é a compra. "Quase sempre" é o que separa as duas janelas:
 * dentro de três dias a tela **sugere**; entre quatro e trinta ela **oferece**,
 * dizendo a distância, e quem decide é quem viu o cupom.
 *
 * Trinta e não noventa porque o falso positivo cresce com o tempo: a mesma
 * compra de mercado repete de mês em mês com valores parecidos, e passar de um
 * ciclo de fatura começaria a casar a nota com a compra do mês errado.
 */
export const JANELA_AMPLA_DIAS = 30

/**
 * As transações que podem ser a desta nota.
 *
 * *"Procura transação com valor igual a `extracted.totalCents` numa janela de ±3
 * dias de `extracted.issuedAt`. Uma candidata → sugere. Nenhuma ou várias → o
 * usuário escolhe."*
 *
 * Devolve **todas** as candidatas, ordenadas da mais próxima; quem decide entre
 * "sugerir" e "perguntar" é a tela, olhando o tamanho da lista. Uma função que
 * devolvesse `Transaction | null` teria de escolher sozinha no empate — e o
 * empate é justamente o caso em que a nota diz para perguntar.
 *
 * **O valor tem de bater exatamente**, em módulo. Uma tolerância aqui casaria a
 * nota de R$ 100,00 com a compra de R$ 100,50 do mesmo dia, e a nota errada
 * viraria o breakdown de uma transação que ela não descreve.
 */
/**
 * Os meses que precisam ser lidos para conciliar uma nota.
 *
 * A janela de `reconcile` é de ±3 dias, e ela **atravessa a virada do mês**: uma
 * nota de 01/09 pode ser a compra lançada em 30/08. Ler só o mês da emissão
 * perderia essa candidata em silêncio — e o silêncio é o problema, porque a tela
 * diria "nenhuma transação parecida" com toda a confiança.
 *
 * Com a janela de três, dá um mês na maioria das vezes e dois perto das bordas.
 * Com a de trinta pode dar **três** — uma nota do meio de setembro alcança
 * agosto e outubro —, e é por isso que isto enumera em vez de comparar as duas
 * pontas: a versão anterior devolvia `[inicio, fim]` e perderia o mês do meio
 * em silêncio, que é o pior jeito de perder uma candidata.
 */
export function monthsToScan(
  issuedAt: CivilDate,
  addDays: (d: CivilDate, n: number) => CivilDate,
  janelaDias: number = JANELA_DIAS,
): string[] {
  const inicio = addDays(issuedAt, -janelaDias)
  const fim = addDays(issuedAt, janelaDias).slice(0, 7)

  const meses: string[] = []
  let dia = inicio
  // Anda de dia em dia porque somar "um mês" a uma data civil é o tipo de conta
  // que erra em 31 de janeiro. São 61 passos no pior caso.
  for (let i = 0; i <= janelaDias * 2; i += 1) {
    const mes = dia.slice(0, 7)
    if (!meses.includes(mes)) meses.push(mes)
    if (mes === fim) break
    dia = addDays(dia, 1)
  }
  return meses
}

export function reconcile(
  extracted: Pick<ExtractedHeader, 'issuedAt' | 'totalCents'>,
  transactions: { id: string; amountCents: Cents; transactionDate: CivilDate }[],
  daysBetween: (a: CivilDate, b: CivilDate) => number,
  janelaDias: number = JANELA_DIAS,
): ReconcileCandidate[] {
  const { issuedAt, totalCents } = extracted
  if (issuedAt === null || totalCents === null) return []

  return transactions
    .filter((t) => Math.abs(t.amountCents) === Math.abs(totalCents))
    .map((t) => ({ transactionId: t.id, daysApart: Math.abs(daysBetween(issuedAt, t.transactionDate)) }))
    .filter((c) => c.daysApart <= janelaDias)
    .sort((a, b) => a.daysApart - b.daysApart || a.transactionId.localeCompare(b.transactionId))
}
